import { useCallback, useMemo, useState } from "react";

type ReviewLineage = {
  lineageId?: string | null;
  versionNumber?: number | null;
};

/**
 * The newest version of each artwork that has two or more versions. Versions
 * share a lineage: the first review's id, or a review's own id when nobody
 * linked it. A lone version is not "latest" of anything, so it is left out.
 */
export function latestReviewIds(
  reviews: readonly ({ id: string } & ReviewLineage)[],
): Set<string> {
  const byLineage = new Map<
    string,
    { id: string; version: number; count: number }
  >();
  for (const item of reviews) {
    // why ?? 1: the first version is often stored with no number, and the
    // one linked after it would otherwise be a lineage of one.
    const version = item.versionNumber ?? 1;
    const lineage = item.lineageId ?? item.id;
    const seen = byLineage.get(lineage);
    byLineage.set(lineage, {
      id: !seen || version > seen.version ? item.id : seen.id,
      version: Math.max(version, seen?.version ?? 0),
      count: (seen?.count ?? 0) + 1,
    });
  }
  const latest = new Set<string>();
  byLineage.forEach((entry) => {
    if (entry.count > 1) latest.add(entry.id);
  });
  return latest;
}

/**
 * The rail's Latest badge. Each output review card reports the lineage it
 * fetched; the list marks the newest of each artwork among the ids it shows.
 * why reported up: most output cards come from chat messages, which carry no
 * lineage, so only the fetched review knows which artwork it belongs to.
 */
export function useLatestReviewIds(shownIds: readonly string[]) {
  const [lineages, setLineages] = useState<Record<string, ReviewLineage>>({});
  const reportLineage = useCallback((id: string, lineage: ReviewLineage) => {
    setLineages((prev) => {
      const seen = prev[id];
      if (
        seen &&
        seen.lineageId === lineage.lineageId &&
        seen.versionNumber === lineage.versionNumber
      ) {
        return prev;
      }
      return { ...prev, [id]: lineage };
    });
  }, []);
  const shownKey = shownIds.join(",");
  const latestIds = useMemo(
    () =>
      latestReviewIds(
        shownKey
          .split(",")
          .filter((id) => id in lineages)
          .map((id) => ({ id, ...lineages[id] })),
      ),
    [lineages, shownKey],
  );
  return { latestIds, reportLineage };
}
